<script>
import {mapGetters} from 'vuex';
import {StudyPeriod} from '@/store/models/study';
import {statusVariant, determineStatus} from '@/helpers/utils';
import {ACTIVE_PERIOD_INTRO_CONFIG, EXPENSE_ENTITY_LABEL_CONFIG} from '@/helpers/baseConfig';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
} from '@/helpers/expenseCompletion';

export default {
   name: 'ActivePeriodTable',
   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },
   computed: {
      ...mapGetters({
         isRndig: 'isRndig',
         isSME: 'isSME',
         profile: 'profile',
         studyPeriodMap: 'companies/studyPeriodMap',
         activeStudyId: 'companies/activeStudyId',
         flags: 'flags/flags',
      }),
      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      manualVendors() {
         return this.$store.getters[`${this.expenseType}/manualVendors`];
      },

      importedVendors() {
         return this.$store.getters[`${this.expenseType}/importedVendors`];
      },

      periodIds() {
         return this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
      },

      hasPeriodIds() {
         return this.periodIds.length > 0;
      },

      activePeriodIntroCopy() {
         return ACTIVE_PERIOD_INTRO_CONFIG[this.expenseType];
      },

      hasAnyVendors() {
         if (this.isSME) {
            return (
               [...this.importedVendors, ...this.manualVendors].filter(
                  ({assigneeId}) => assigneeId === this.profile.id
               ).length !== 0
            );
         }
         return this.importedVendors.length !== 0 || this.manualVendors.length !== 0;
      },

      entityLabelPluralLower() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.pluralLower ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.pluralLower
         );
      },

      noAssignedEntityLabel() {
         return `No assigned ${this.entityLabelPluralLower}`;
      },

      fields() {
         return [
            StudyPeriod.tableField(),
            {key: 'status', class: 'no-wrap', sortable: false},
            {key: 'actions', label: ''},
         ];
      },

      items() {
         if (!this.hasAnyVendors) {
            return [];
         }

         return this.periodIds
            .map((periodId) => {
               const period = this.studyPeriodMap[periodId];
               const status = this.mergedStatusesBy(periodId);
               const hasVendors = this.hasAssignedVendorsForPeriod(periodId);

               return {periodId, period, status, hasVendors};
            })
            .filter(({periodId}) => this.hasVendorsForPeriod(periodId));
      },
   },

   methods: {
      // Employees have no backend auto-expansion of summary rows across periods, and
      // state.manualVendors/importedVendors are keyed by employee id, so committing the
      // all-assignments response collapses each employee down to whichever period's row
      // was committed last -- filtering the roster by matching its own (stale/arbitrary)
      // `.period` field against the period being checked silently drops employees who
      // are genuinely assigned in THIS period but happened to lose the roster collapse.
      // importedVendorsByPeriod/manualVendorsByPeriod (expense-data.js) already
      // cross-reference adHocData instead of trusting the stored `.period` -- use those
      // here too, same as TimePeriodTable.vue's vendorFields does.
      getImportedVendorsBy(periodId) {
         const vendors =
            this.$store.getters[`${this.expenseType}/importedVendorsByPeriod`](periodId);
         if (this.isSME) {
            return vendors.filter(({assigneeId}) => assigneeId === this.profile.id);
         }
         return vendors;
      },

      getManualVendorsBy(periodId) {
         const vendors = this.$store.getters[`${this.expenseType}/manualVendorsByPeriod`](periodId);
         if (this.isSME) {
            return vendors.filter(({assigneeId}) => assigneeId === this.profile.id);
         }
         return vendors;
      },

      hasVendorsForPeriod(periodId) {
         return (
            !!this.getManualVendorsBy(periodId).length ||
            !!this.getImportedVendorsBy(periodId).length
         );
      },

      hasAssignedVendorsForPeriod(periodId) {
         const vendors = [
            ...this.getImportedVendorsBy(periodId),
            ...this.getManualVendorsBy(periodId),
         ];
         // getImportedVendorsBy/getManualVendorsBy already scope to the SME's own
         // assigneeId -- a non-empty result IS the assignment for them.
         if (this.isSME) {
            return vendors.length > 0;
         }
         return vendors.some(({assigneeId}) => assigneeId !== null);
      },
      // Contractors don't support data-check flags at all (see ExpensesCard.vue's
      // loadFlag()) -- SME's own view is already restricted to their own assignments
      // everywhere else on this page, so scope the flags check the same way; everyone
      // else (staff/customer) sees this period's combined status across all assignees,
      // so their flags check should span all assignees too.
      hasUnresolvedFlagsForPeriod(periodId) {
         if (this.expenseType === 'contractors') return false;

         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            period: this.studyPeriodMap[periodId]?.period,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
            includeAllUsers: !this.isSME,
         });

         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      // Delegate to the same Vuex getter the Period page (ExpensesCard.vue) uses,
      // rather than re-deriving statuses here -- a second, hand-rolled
      // implementation of this computation drifted out of sync with the real
      // one (missing assignee-scoping, placeholder-row filtering, and stale
      // project/activity id handling), showing "In Progress" here while the
      // Period page correctly showed "Complete" for the same period.
      // A period with unresolved flags is never shown as Complete here, even if its
      // data is otherwise fully filled in -- flags mean something still needs review/
      // action before this period is genuinely done, and "Complete" would say otherwise.
      mergedStatusesBy(periodId) {
         const statusesByAssignee =
            this.$store.getters[`${this.expenseType}/mergedStatusesByPeriod`](periodId);
         const status = determineStatus(Object.values(statusesByAssignee));
         const {COMPLETE, IN_PROGRESS} = this.$constants().StatusType;

         if (status === COMPLETE && this.hasUnresolvedFlagsForPeriod(periodId)) {
            return IN_PROGRESS;
         }

         return status;
      },

      getColorByStatus(value) {
         return statusVariant(value);
      },

      handleEnterTime(periodId) {
         this.$router.push({
            name: this.isRndig
               ? `${this.expenseType}-period-review-staff`
               : `${this.expenseType}-period-review`,
            params: {
               periodId,
               id: this.$companyId,
            },
         });
      },
   },
};
</script>

<template>
   <b-card v-if="hasAnyVendors" class="mt-2">
      <h2 v-if="hasPeriodIds" class="card-title mb-1">
         {{ activePeriodIntroCopy.title }}
      </h2>
      <p
         v-if="hasPeriodIds"
         class="card-description mb-4"
         v-html="activePeriodIntroCopy.description"
      ></p>
      <b-table
         :fields="fields"
         sort-by="period"
         :items="items"
         id="table-time-survey"
         class="mb-3 active-period-table"
      >
         <template #cell(status)="data">
            <span v-if="data.item.hasVendors" :class="`text-${getColorByStatus(data.value)}`">
               {{ data.value }}
            </span>
            <span v-else class="text-muted">{{
               hasVendorsForPeriod(data.item.periodId)
                  ? noAssignedEntityLabel
                  : 'No qualified expenses yet.'
            }}</span>
         </template>

         <template #cell(actions)="data">
            <div class="cell-w-buttons d-flex justify-content-end">
               <b-button
                  id="btn-start"
                  variant="primary"
                  size="sm"
                  style="font-size: 0.625rem"
                  class="btn-w-icon button--small"
                  @click="() => handleEnterTime(data.item.periodId)"
               >
                  Enter Time
               </b-button>
            </div>
         </template>
      </b-table>
   </b-card>
</template>

<style scoped lang="scss">
.declaration-container {
   gap: 1rem;
}
.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}

.active-period-table {
   font-size: 13px;
   border-top: 1px solid #e2e2e2;
   border-bottom: 1px solid #e2e2e2;
}
</style>
